import { useEffect, useRef, useState } from 'react';
import { useEnterToClick } from '../../../hooks/useEnter';
import InputField from '../../Forms/Input/InputField';
import SelectField from '../../Forms/Input/SelectField';
import { useCashRegister } from '../../../hooks/cash/useCashRegister';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';

interface BranchOption {
  id: number;
  name: string;
}

interface Props {
  onSubmit: (data: any) => void;
  branchOptions?: BranchOption[];
}

const CashRegisterForm = ({ onSubmit, branchOptions = [] }: Props) => {
  const saveBtnRef = useRef<HTMLButtonElement>(null);
  useEnterToClick(saveBtnRef, true);

  const { form, updateField, clearForm } = useCashRegister();
  const { myBranch, fetchMyBranch } = useBranch();
  const { hasRole } = useAuthUser();

  // Solo estos roles operan/configuran sucursales distintas a la propia; para
  // el resto la sucursal se toma siempre de la del usuario, sin mostrar el
  // selector.
  const canPickBranch = hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchMyBranch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Precarga la sucursal del usuario por defecto (editable si tiene el rol).
  useEffect(() => {
    if (myBranch && !form.branchId) {
      updateField('branchId' as any, myBranch.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBranch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    updateField(name as any, value);
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === 'branchId') {
      updateField(name as any, Number(value));
      return;
    }

    updateField(name as any, value);
  };

  const resetForm = () => {
    clearForm();
    setErrors({});
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name) newErrors.name = 'Nombre requerido';
    if (!form.code) newErrors.code = 'Código requerido';
    if (!form.branchId) newErrors.branchId = 'Sucursal requerida';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      name: form.name,
      code: form.code,
      branchId: form.branchId,
    };

    onSubmit(payload);

    resetForm();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <InputField
          label="Nombre de Terminal"
          name="name"
          iconKey="terminal"
          value={form.name}
          onChange={handleChange}
          error={errors.name}
          type="text"
          required
        />

        <InputField
          label="Código"
          name="code"
          iconKey="barcode"
          value={form.code}
          onChange={handleChange}
          error={errors.code}
          type="text"
          required
        />

        {canPickBranch && (
          <SelectField
            label="Sucursal"
            name="branchId"
            iconKey="branch"
            value={form.branchId ? String(form.branchId) : ''}
            onChange={(e: any) => handleSelectChange('branchId', e.target.value)}
            options={branchOptions.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
            error={errors.branchId}
            required
          />
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          ref={saveBtnRef}
          onClick={handleSave}
          className="inline-flex items-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          Guardar
        </button>

        <button
          type="button"
          onClick={resetForm}
          className="inline-flex items-center rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default CashRegisterForm;
